// ============================================
// StatusPipelineCards - 5 status cards showing order pipeline
// Proposed → Accepted → Preparing → In Transit → Delivered
// ============================================

import { useI18n } from '../../../i18n';
import { cn } from '../../../utils/cn';

// ============================================
// Types
// ============================================

interface StatusCardData {
  key: string;
  label: string;
  value: number;
  badgeText: string;
  borderColor: string;
  badgeColor: string;
  icon: React.ReactNode;
}

interface StatusPipelineCardsProps {
  stats: {
    pending: number;
    confirmed: number;
    processing: number;
    ready: number;
    completed: number;
    cancelled: number;
    total: number;
  };
  activeStatus?: string;
  onStatusClick: (status: string | null) => void;
  isLoading?: boolean;
}

// ============================================
// Icons
// ============================================

function DocumentIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

function CheckCircleIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function CubeIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
        d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    </svg>
  );
}

function TruckIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
        d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
    </svg>
  );
}

function CheckBadgeIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
        d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
    </svg>
  );
}

// ============================================
// Status Card Component
// ============================================

function StatusCard({
  data,
  isActive,
  onClick,
}: {
  data: StatusCardData;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'relative flex flex-col bg-white rounded-xl shadow-sm border-l-4 h-[140px] p-5 text-left transition-all',
        'hover:shadow-md hover:scale-[1.02]',
        isActive && 'ring-2 ring-primary-500 ring-offset-2',
        data.borderColor
      )}
    >
      {/* Icon */}
      <div 
        className={cn(
          'w-9 h-9 rounded-lg flex items-center justify-center',
          data.key === 'pending' && 'bg-blue-100 text-blue-600',
          data.key === 'confirmed' && 'bg-emerald-100 text-emerald-600',
          data.key === 'processing' && 'bg-amber-100 text-amber-600',
          data.key === 'ready' && 'bg-cyan-100 text-cyan-700',
          data.key === 'completed' && 'bg-emerald-100 text-emerald-600',
        )}
      >
        {data.icon}
      </div>

      {/* Badge */}
      <span 
        className={cn(
          'absolute top-4 right-4 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase',
          data.badgeColor
        )}
      >
        {data.badgeText}
      </span>

      {/* Label */}
      <span className="mt-auto text-sm font-medium text-[#434655]">
        {data.label}
      </span>

      {/* Value */}
      <span className="text-lg font-bold text-[#191B23] sm:text-2xl">
        {data.value.toLocaleString()}
      </span>
    </button>
  );
}

// ============================================
// Loading Skeleton
// ============================================

function StatusCardSkeleton() {
  return (
    <div className="relative flex flex-col bg-white rounded-xl shadow-sm border-l-4 border-slate-200 h-[140px] p-5 animate-pulse">
      <div className="w-9 h-9 rounded-lg bg-slate-100" />
      <div className="absolute top-4 right-4 w-16 h-4 rounded-full bg-slate-100" />
      <div className="mt-auto w-20 h-4 rounded bg-slate-100" />
      <div className="w-12 h-7 rounded bg-slate-200 mt-1" />
    </div>
  );
}

// ============================================
// Main Component
// ============================================

export function StatusPipelineCards({
  stats,
  activeStatus,
  onStatusClick,
  isLoading = false,
}: StatusPipelineCardsProps) {
  const { t } = useI18n();
  const statusCards: StatusCardData[] = [
    {
      key: 'pending',
      label: t('pending'),
      value: stats.pending,
      badgeText: stats.pending > 0 ? `${stats.pending} ${t('newWord')}` : t('none'),
      borderColor: 'border-l-[#3B82F6]',
      badgeColor: 'bg-blue-100 text-blue-600',
      icon: <DocumentIcon />,
    },
    {
      key: 'confirmed',
      label: t('confirmed'),
      value: stats.confirmed,
      badgeText: stats.confirmed > 0 ? `${stats.confirmed} ${t('approved')}` : t('none'),
      borderColor: 'border-l-[#10B981]',
      badgeColor: 'bg-emerald-100 text-emerald-600',
      icon: <CheckCircleIcon />,
    },
    {
      key: 'processing',
      label: t('processing'),
      value: stats.processing,
      badgeText: stats.processing > 0 ? t('inProgress') : t('none'),
      borderColor: 'border-l-[#F59E0B]',
      badgeColor: 'bg-amber-100 text-amber-700',
      icon: <CubeIcon />,
    },
    {
      key: 'ready',
      label: t('ready'),
      value: stats.ready,
      badgeText: stats.ready > 0 ? t('waitingPickup') : t('none'),
      borderColor: 'border-l-[#0EA5E9]',
      badgeColor: 'bg-cyan-100 text-cyan-700',
      icon: <TruckIcon />,
    },
    {
      key: 'completed',
      label: t('completed'),
      value: stats.completed,
      badgeText: stats.completed > 0 ? `${stats.completed} ${t('done')}` : t('none'),
      borderColor: 'border-l-[#10B981]',
      badgeColor: 'bg-emerald-100 text-emerald-600',
      icon: <CheckBadgeIcon />,
    },
  ];

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        {[1, 2, 3, 4, 5].map((i) => (
          <StatusCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
      {statusCards.map((card) => (
        <StatusCard
          key={card.key}
          data={card}
          isActive={activeStatus === card.key}
          onClick={() => onStatusClick(activeStatus === card.key ? null : card.key)}
        />
      ))}
    </div>
  );
}
